import React, {JSX, useState} from "react";
import SelfcarePageContent from "./SelfcarePageContent";
import {selfcareReportOffline} from "../../util/request.ts";
import {Customer} from "../../util/types";
import {phoneNumber} from "../../util/formatter";
import {useCustomer} from "../../layouts/SelfcareLayout.tsx";
import Icon from "./Icon";
import {Link} from "react-router-dom";
import IconAlert from "../icons/IconAlert.tsx";

export default function ReportOffline() {

    return (
        <SelfcarePageContent className={'selfcare-page-content--wide selfcare-page-report-connection'}>
            <section className="selfcare-report-connection">
                <h2 className="heading heading--montserrat">
                    Er dit internet nede?
                </h2>
                <p>
                    Det er vi kede af &ndash; det finder vi selvfølgelig en løsning på!
                    Tryk på knappen, og vi vil hjælpe dig på rette vej.
                </p>
                <ReportOfflineButton/>
            </section>
        </SelfcarePageContent>
    );
}

const ReportOfflineButton = () => {
    const [popUpOpen, setPopUpOpen] = useState<boolean>(false);

    const onClick = () => {
        setPopUpOpen(!popUpOpen);
    };

    return (
        <div className={'buttons'}>
            <button
                className={'fejlmelding-startet'}
                onClick={onClick}
            >
                <span className="text">
                    Start fejlmelding
                </span>
                <Icon component={IconAlert}/>
            </button>
            {popUpOpen && <ReportOfflinePopUpContainer toggleReportOfflinePopUp={onClick}/>}
        </div>
    );
};

const ReportOfflinePopUpContainer = ({toggleReportOfflinePopUp}: { toggleReportOfflinePopUp: () => void }) => {
    const [step, setStep] = useState<number>(0);
    const [textareaValue, setTextareaValue] = useState<string>("");
    const [checkbox1, setCheckbox1] = useState<boolean>(false);
    const [checkbox2, setCheckbox2] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
    const [httpResponse, setHttpResponse] = useState<number | null>(null);

    const {customer} = useCustomer()

    const handleContinue = () => {
        setStep(step + 1);
    };

    const handleBack = () => {
        setStep(step - 1);
    };

    const handleBackToForm = () => {
        setStep(1);
    };

    const handleTextareaChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setTextareaValue(event.target.value);
    };
    const handleCheckbox1Change = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCheckbox1(event.target.checked);
    };

    const handleCheckbox2Change = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCheckbox2(event.target.checked);
    };

    const validateForm = () => {
        if (!checkbox1 && !checkbox2) {
            setError("Du mangler at vælge hvordan vi skal kontakte dig.");
            return false;
        }
        setError("");
        return true;
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!validateForm()) {
            return;
        }
        setHasSubmitted(true);

        selfcareReportOffline(customer.customer_id, textareaValue, checkbox2, checkbox1).then(r => {
            setHttpResponse(r.code);
            if (r.code === 429 || r.code !== 200) {
                setStep(3);
                setHasSubmitted(false);
                return;
            }
            setStep(2);

        }).catch(() => {
            setHttpResponse(500);
            setStep(3);
            setHasSubmitted(false);
        });
    };

    const directionProps = {
        toggleReportOfflinePopUp: toggleReportOfflinePopUp,
        handleContinue: handleContinue,
    };

    const formProps = {
        textareaValue: textareaValue,
        handleTextareaChange: handleTextareaChange,
        checkbox1: checkbox1,
        handleCheckbox1Change: handleCheckbox1Change,
        checkbox2: checkbox2,
        handleCheckbox2Change: handleCheckbox2Change,
        error: error,
        handleBack: handleBack,
        handleSubmit: handleSubmit,
        hasSubmitted: hasSubmitted,
        customer: customer
    };

    switch (step) {
        case 0:
            return (
                <ReportOfflinePopUp
                    className={'step-start'}
                    onOverlayClick={() => toggleReportOfflinePopUp()}
                >
                    {renderInfoStep(directionProps)}
                </ReportOfflinePopUp>
            );

        case 1:
            return (
                <ReportOfflinePopUp
                    className={'step-form'}
                    onOverlayClick={() => toggleReportOfflinePopUp()}
                >
                    {renderOfflineFormStep(formProps)}
                </ReportOfflinePopUp>
            );

        case 2:
            return (
                <ReportOfflinePopUp
                    className={'step-success'}
                    onOverlayClick={() => toggleReportOfflinePopUp()}
                >
                    {renderSuccessStep(toggleReportOfflinePopUp)}
                </ReportOfflinePopUp>
            );

        case 3:
            return (
                <ReportOfflinePopUp
                    className={'step-error'}
                    onOverlayClick={() => toggleReportOfflinePopUp()}
                >
                    {httpResponse !== null ? renderErrorStep(httpResponse, handleBackToForm) : <></>}
                </ReportOfflinePopUp>
            );
        default:
    }
};

const renderInfoStep = ({toggleReportOfflinePopUp, handleContinue}: {
    toggleReportOfflinePopUp: () => void,
    handleContinue: () => void
}) => {
    return (
        <>
            <h3 className="heading">
                Først skal du tjekke 3 ting
            </h3>
            <p className="attention-box">
                <strong>OBS:</strong> Du skal kun fortsætte, hvis dit internet slet ikke virker.
                Er dit internet ustabilt, kan du{' '}
                <Link to="/hjaelp/internet-og-wifi/ustabilt-internet" target="_blank">
                    finde hjælp her
                </Link>.
            </p>
            <ul className={'number'}>
                <li>
                    Tjek at din router er tændt: Power-lampen skal lyse grønt.
                </li>
                <li>
                    Tjek at alle kabler sidder korrekt i routeren.
                </li>
                <li>
                    Tjek om der er{' '}
                    <a href="https://drift.hiper.dk" target="_blank">
                        driftsforstyrrelser i dit område
                    </a>.
                </li>
            </ul>
            <div className="buttons">
                <button className="cancel" onClick={() => toggleReportOfflinePopUp()}>Annullér</button>
                <button className="confirm fejlmelding-fortsat" onClick={handleContinue}>Næste</button>
            </div>
        </>
    );
}

const renderOfflineFormStep = ({
                                  textareaValue,
                                  handleTextareaChange,
                                  checkbox1,
                                  handleCheckbox1Change,
                                  checkbox2,
                                  handleCheckbox2Change,
                                  error,
                                  handleBack,
                                  handleSubmit,
                                  hasSubmitted,
                                  customer,
                              }: {
    textareaValue: string,
    handleTextareaChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void,
    checkbox1: boolean,
    handleCheckbox1Change: (event: React.ChangeEvent<HTMLInputElement>) => void,
    checkbox2: boolean,
    handleCheckbox2Change: (event: React.ChangeEvent<HTMLInputElement>) => void,
    error: string,
    handleBack: () => void,
    handleSubmit: (event: React.FormEvent<HTMLFormElement>) => void,
    hasSubmitted: boolean,
    customer: Customer
}) => {

    return (<>
        <h3 className="heading">
            Opret en fejlmelding til vores support
        </h3>
        <form onSubmit={handleSubmit}>
            <p>
                Uddyb dit problem i formularen og vælg, hvordan vi skal kontakte dig for at finde en løsning.
            </p>
            <div className={'input-container'}>
                <textarea
                    value={textareaValue}
                    onChange={handleTextareaChange}
                    placeholder="Eks. &ldquo;Ingen af mine enheder har kunnet tilgå internettet siden i går&rdquo;"
                    required
                />
            </div>
            <div className={'input-container'}>
                <p>Hvordan vil du kontaktes?</p>
                <div className={'contact-row'}>
                    <div className={'contact-item'}>
                        <input
                            type="checkbox"
                            id="contact-email"
                            checked={checkbox1}
                            onChange={handleCheckbox1Change}
                        />
                        <label
                            htmlFor="contact-email"
                            className="checkbox-label checkbox-label--box checkbox-label--montserrat"
                        >
                            Mail<br/>
                            <small>{customer.customer_contact.email}</small>
                        </label>
                    </div>
                    <div className={'contact-item'}>
                        <input
                            type="checkbox"
                            id="contact-phone"
                            checked={checkbox2}
                            onChange={handleCheckbox2Change}
                        />
                        <label
                            htmlFor="contact-phone"
                            className="checkbox-label checkbox-label--box checkbox-label--montserrat"
                        >
                            Telefon<br/>
                            <small>{phoneNumber(customer.customer_contact.mobile_no)}</small>
                        </label>
                    </div>
                </div>
                <p className={'small'}>
                    <strong>Tip:</strong> Du kan vælge begge kontakt&shy;muligheder, hvis du ikke har nogen præference.
                </p>
            </div>
            <div className={'input-container'}>
                <p className={'with-checkbox'}>
                    <input
                        type="checkbox"
                        id="completed-troubleshooting"
                        required
                    />
                    <label
                        htmlFor="completed-troubleshooting"
                        className="checkbox-label checkbox-label--montserrat"
                    >
                        Jeg har tjekket de 3 ting, og mit internet virker stadig ikke.
                    </label>
                </p>
            </div>
            {error && <p className="error-message">{error}</p>}
            <div className="buttons">
                <button className="cancel" onClick={handleBack}>Tilbage</button>
                <button className="confirm fejlmelding-sendt" disabled={hasSubmitted} type="submit">
                    {hasSubmitted ? "Sender" : "Send til support"}
                </button>
            </div>
        </form>
    </>);
}

const renderSuccessStep = (toggleReportOfflinePopUp: () => void) => {
    return (<>
        <h3 className="heading">
            Tak, din besked er blevet sendt
        </h3>
        <p>
            En af vores dygtige supportere vil kontakte dig snarest muligt.
            Vi beklager endnu en gang og ser frem til at hjælpe dig.
        </p>
        <div className="buttons">
            <button className="confirm" onClick={() => toggleReportOfflinePopUp()}>Luk vindue</button>
        </div>
    </>);
}

const renderErrorStep = (errorCode: number, handleBackToForm: () => void) => {

    let errorMessage: JSX.Element;

    switch (errorCode) {
        case 400:
            errorMessage = (<>
                Vi kunne ikke behandle din besked. Prøv venligst igen og undgå evt. specialtegn mm.
            </>);
            break;
        case 429:
            errorMessage = (<>
                Du har allerede for nyligt sendt os en besked, som vi er ved at behandle.
            </>);
            break;
        case 500:
        default:
            errorMessage = (<>
                Din besked kunne desværre ikke sendes på nuværende tidspunkt.<br/>
                Prøv igen senere eller kontakt os på{' '}
                <a href="mailto:kundeservice@hiper.dk">kundeservice@hiper.dk</a> eller <a href="tel:+4588772000">88&nbsp;77&nbsp;20&nbsp;00</a>.
            </>);
            break;
    }

    return (
        <>
            <h3 className="heading">
                Hov, der skete en fejl
            </h3>
            <p>
                {errorMessage}
            </p>
            <div className="buttons">
                <button className="cancel" onClick={handleBackToForm}>Tilbage</button>
            </div>
        </>
    );
};

interface ReportOfflinePopUpProps {
    children: string | JSX.Element | JSX.Element[]
    className?: string
    onOverlayClick: () => void
}

const ReportOfflinePopUp = ({children, className, onOverlayClick}: ReportOfflinePopUpProps) => {
    return (<>
        <div className="selfcare-popup-overlay" onClick={onOverlayClick}></div>
        <div className={`selfcare-popup selfcare-popup--report-connection ${className ?? ''}`}>
            {children}
        </div>
    </>);
};
